import type { QueryClient } from '@tanstack/react-query';
import type {
  VonqDirectApplyQuestionnairePreview,
  VonqPostingRequirementField,
} from 'api-clients/monolith';
import { defineMessages } from 'react-intl';

import { queryKeys } from '@/query-keys';

import { LoadedOptions, shouldShowField } from './display-rules';
import type { PostingRequirementsDef } from './requirements-key';

type RequirementValues = Record<string, string | string[] | null | undefined>;

export const DIRECT_APPLY_QUESTIONNAIRE_CHANGED =
  'direct_apply_questionnaire_changed';

export const directApplyQuestionnaireMessages = defineMessages({
  questionsChanged: {
    defaultMessage:
      'These questions changed. Refresh this page to see the current ones.',
    description:
      "Shown when Hire refused a job board order because the opening's screening questions changed after the recruiter reviewed them",
  },
});

/** The board's first QUESTIONNAIRE field by `sort`. */
export function questionnaireFacet(
  def: PostingRequirementsDef,
): VonqPostingRequirementField | undefined {
  return def.posting_requirements
    .filter(f => f.type === 'QUESTIONNAIRE')
    .sort((a, b) =>
      a.sort.localeCompare(b.sort, undefined, { numeric: true }),
    )[0];
}

// The cached preview while the facet is visible (Direct Apply selected) and
// Hire built a questionnaire. A flag-off preview carries no `facet`.
function visiblePreview(
  def: PostingRequirementsDef,
  values: RequirementValues,
  loadedOptions: LoadedOptions,
  queryClient: QueryClient,
  openingId: string,
) {
  const facet = questionnaireFacet(def);
  if (
    !facet ||
    !shouldShowField(facet, values, def.posting_requirements, loadedOptions)
  )
    return undefined;
  const preview = queryClient.getQueryData<VonqDirectApplyQuestionnairePreview>(
    queryKeys.jobPosting.questionnairePreview(openingId, def.key),
  );
  return preview?.facet ? preview : undefined;
}

/** The order's `direct_apply_questionnaire`, or `undefined` to omit it. */
export function directApplyQuestionnaire(
  ...args: Parameters<typeof visiblePreview>
): { facet: string; value: string } | undefined {
  const preview = visiblePreview(...args);
  if (!preview?.facet || preview.value === undefined) return undefined;
  return { facet: preview.facet, value: preview.value };
}

export function isQuestionnaireRejected(
  ...args: Parameters<typeof visiblePreview>
): boolean {
  return visiblePreview(...args)?.valid === false;
}

export function isQuestionnaireChanged(
  errors: Record<string, string[] | undefined> | null | undefined,
): boolean {
  return (
    errors?.direct_apply_questionnaire?.includes(
      DIRECT_APPLY_QUESTIONNAIRE_CHANGED,
    ) ?? false
  );
}
